import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { lazy, Suspense, useContext, useEffect, useState } from 'react';
import { GoogleOAuthProvider } from '@react-oauth/google';
import '@chatscope/chat-ui-kit-styles/dist/default/styles.min.css';
import 'react-toastify/dist/ReactToastify.css';
import SlackSelect from './components/AppDrawer/Slack/SlackSelect';
import TeamsSelect from './components/AppDrawer/Teams/TeamsSelect';
import DiscordSelect from './components/AppDrawer/Discord/DiscordSelect';
import TelegramSelect from './components/AppDrawer/Telegram/TelegramSelect';
import TrelloSelect from './components/AppDrawer/Trello/TrelloSelect';
import AirtableSelect from './components/AppDrawer/Airtable/AirtableSelect';
import GlobalIntroGuide from './layouts/GlobalIntroGuide';
import ManageLeave from './pages/Leave/ManageLeave';
import Screenloader from './components/screenLoader/Screenloader';
import { ChatSessionProvider } from './components/context/ChatSessionContext';
import { QueryClientProvider } from '@tanstack/react-query';
import { QueryCache, QueryClient } from '@tanstack/react-query';
import { userContext } from './UserContext';
import importRetry from './importRetry';
import FolderSelect from './components/AppDrawer/Google/FolderSelect';
import lazyWithRetry from './lazyWithRetry';
import axios from 'axios';
import { AUTH_URL } from './features/apis/baseFectch';
import ChatBot from './components/Chatbot/Chatbot';
import TermsModal from './pages/Terms/TermsModal';
import PaymentSuccess from './components/Plans/Finish/PaymentSuccess';
import PaymentFail from './components/Plans/Finish/PaymentFail';
import ProtectedRoute from './privateRoutes/ProtectedRoute';
import AiRecruiter from './pages/AiRecruiter/AiRecruiter';
import TaraAI from './pages/AiHR/TaraAI';
import Consent from './components/Settings/Consent';
import AccessModal from './pages/Modal/AccessModal';
export const QUERY_CLIENT = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
  queryCache: new QueryCache({
    onError: (_error, query) => {
      if (query.state.data !== undefined) {
        console.error(`Something went wrong`);
      }
    },
  }),
});

const ToastContainer = lazy(() =>
  import('react-toastify').then((mod) => ({
    default: mod.ToastContainer,
  }))
);
const lazyRoutes = {
  MyLeave: lazyWithRetry(() =>
    importRetry(() => import('./pages/Leave/MyLeave'))
  ),
  Subscription: lazyWithRetry(() =>
    importRetry(() => import('./pages/Subscription/Subscription'))
  ),
  Employees: lazyWithRetry(() =>
    importRetry(() => import('./pages/Employees/Employees'))
  ),

  Layout: lazyWithRetry(() => importRetry(() => import('./layouts/'))),
  Home: lazyWithRetry(() =>
    importRetry(() => import('./components/Home/Home'))
  ),
  Profile: lazyWithRetry(() =>
    importRetry(() => import('./pages/Profile/Profile'))
  ),
  PolicyDocs: lazyWithRetry(() =>
    importRetry(() => import('./pages/PolicyManager/PolicyDocs'))
  ),

  AttendancePortal: lazyWithRetry(() =>
    importRetry(() => import('./pages/AttendancePortal/Portal'))
  ),
  AttendanceList: lazyWithRetry(() =>
    importRetry(() => import('./pages/AttendanceList/AttendanceList'))
  ),
  AttendenceDetail: lazyWithRetry(() =>
    importRetry(() => import('./pages/AttendanceList/ViewAttendenceDetails'))
  ),
  UsersOnline: lazyWithRetry(() =>
    importRetry(() => import('./pages/OnlineUsers/OnlineUsers'))
  ),
  Regularize: lazyWithRetry(() =>
    importRetry(() => import('./pages/Regularize/Regularize'))
  ),
  Payroll: lazyWithRetry(() =>
    importRetry(() => import('./pages/Payroll/Payroll'))
  ),

  CheckAuthRoute: lazyWithRetry(() =>
    importRetry(() => import('./privateRoutes/checkAuthRoute'))
  ),
  Login: lazyWithRetry(() => importRetry(() => import('./pages/Login/Login'))),
  Signup: lazyWithRetry(() =>
    importRetry(() => import('./pages/Signup/Signup'))
  ),

  AssignRoles: lazyWithRetry(() =>
    importRetry(() => import('./components/Admin/AssignRoles/AssignRoles'))
  ),
  UpdatePermissions: lazyWithRetry(() =>
    importRetry(() => import('./pages/Admin/UpdatePermissions'))
  ),
  MonthlyView: lazyWithRetry(() =>
    importRetry(() => import('./pages/AttendanceList/MonthlyView'))
  ),

  SlackActivity: lazyWithRetry(() =>
    importRetry(() => import('./pages/SlackActivity/SlackActivity'))
  ),

  Calendar: lazyWithRetry(() =>
    importRetry(() => import('./pages/Calendar/Calendar'))
  ),
  CompanyForm: lazyWithRetry(() =>
    importRetry(() => import('./pages/Company/CompanyForm'))
  ),
  PageNotFound: lazyWithRetry(() =>
    importRetry(() => import('./pages/PageNotFound/PageNotFound'))
  ),
  uploadDocs: lazyWithRetry(() =>
    importRetry(() => import('./pages/Company/UploadDocs'))
  ),
  EmployeeForm: lazyWithRetry(() =>
    importRetry(() => import('./pages/Employees/EmployeeForm'))
  ),
  Grievances: lazyWithRetry(() =>
    importRetry(() => import('./pages/Requests/Grievances'))
  ),
  ProfileEditRequest: lazyWithRetry(() =>
    importRetry(() => import('./pages/Requests/EditRequest'))
  ),
  Gallery: lazyWithRetry(() =>
    importRetry(() => import('./pages/Gallery/Gallery'))
  ),
  Privacy: lazyWithRetry(() =>
    importRetry(() => import('./pages/Terms/PrivacyPage'))
  ),
  Unauthorized: lazyWithRetry(() =>
    importRetry(() => import('./pages/Unauthorized/Unauthorized'))
  ),
  Settings: lazyWithRetry(() =>
    importRetry(() => import('./pages/Settings/Settings'))
  ),
  TokenUsage: lazyWithRetry(() =>
    import('./pages/TokenUsage/TokenUsage')
  ),
  Analytics: lazyWithRetry(() =>
    import('./pages/Analytics/Analytics')
  ),
  Projects: lazyWithRetry(() =>
    import('./pages/Projects/Projects')
  ),
  Tasks: lazyWithRetry(() =>
    import('./pages/Tasks/Tasks')
  ),
};

const App: React.FC = () => {
  const { user, loading } = useContext(userContext);
  const [isTermsOpen, setIsTermsOpen] = useState(false);
  const [isConsentOpen, setIsConsentOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const location = useLocation();
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isAccessModalOpen, setIsAccessModalOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const storedCode = localStorage.getItem('access_code');

    if (!storedCode) {
      setIsAccessModalOpen(true);
      return;
    }

    axios
      .post(`${AUTH_URL}/access-codes/check`, { code: storedCode })
      .then(() => {
        localStorage.setItem('access_code', storedCode);
      })
      .catch(() => {
        localStorage.removeItem('access_token');
        localStorage.removeItem('access_code');
        setIsAccessModalOpen(true);
      });
  }, []);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('jwtToken');
    if (token) {
      navigate('/');
    }
  }, [location.search, navigate]);
  useEffect(() => {
    const urlHash = window.location.hash;
    if (urlHash.includes('token=')) {
      const params = new URLSearchParams(window.location.hash.substring(1));
      const token = params.get('token');

      if (token) {
        sessionStorage.setItem('trello_token', token);

        setTimeout(() => {
          window.history.replaceState(null, '', window.location.pathname);
        }, 100);
      }
    }
  }, []);
  useEffect(() => {
    if (
      location.pathname !== '/' &&
      location.pathname.startsWith('/dashboard')
    ) {
      localStorage.setItem('currentPath', location.pathname);
    }
  }, [location]);
  useEffect(() => {
    if (!user?._id) return;
    const accepted = localStorage.getItem('termsAccepted');
    if (!accepted) {
      setIsTermsOpen(true);
    }
  }, [user?._id]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const codeFromUrl = urlParams.get('code');
    if (codeFromUrl) {
      sessionStorage.setItem('google_auth_code', codeFromUrl);
      setTimeout(() => {
        window.history.replaceState(null, '', window.location.pathname);
      }, 100);
    }
  }, []);
  useEffect(() => {
    const employeeId = user?._id;
    const companyId = user?.companyId;
    const storedCode = sessionStorage.getItem('google_auth_code');

    if (employeeId && companyId && storedCode) {
      setIsLoading(true);
      axios
        .post(
          `${AUTH_URL}/google/callback/${employeeId}/${companyId}`,
          { code: storedCode },
          { withCredentials: true }
        )
        .then((res) => {
          sessionStorage.removeItem('google_auth_code');
          if (res.data.url) {
            window.location.href = res.data.url;
          }
          setIsLoading(false);
        })
        .catch((err) => {
          console.error('Error fetching folders:', err);
          setIsLoading(false);
        });
    }
  }, [user?._id, user?.companyId]);

  if (loading || isLoading) return <Screenloader />;

  return (
    <div>
      {user?.email && user?.role?.toLowerCase?.().includes('employee') && (
        <ChatBot isOpen={isChatOpen} setIsOpen={setIsChatOpen} />
      )}
      <GlobalIntroGuide />
      <Suspense fallback={<Screenloader />}>
        <Routes>
          <Route
            path="/"
            element={
              <lazyRoutes.CheckAuthRoute>
                <lazyRoutes.Login />
              </lazyRoutes.CheckAuthRoute>
            }
          />
          <Route
            path="/signup"
            element={
              <lazyRoutes.CheckAuthRoute>
                <lazyRoutes.Signup />
              </lazyRoutes.CheckAuthRoute>
            }
          />
          <Route path="/employee" element={<lazyRoutes.EmployeeForm />} />
          <Route path="/channels" element={<SlackSelect />} />
          <Route path="/teams-select" element={<TeamsSelect />} />
          <Route path="/discord-select" element={<DiscordSelect />} />
          <Route path="/telegram-select" element={<TelegramSelect />} />
          <Route path="/trello-select" element={<TrelloSelect />} />
          <Route path="/airtable-select" element={<AirtableSelect />} />
          <Route path="/google-drive" element={<FolderSelect />} />
          <Route path="/company" element={<lazyRoutes.CompanyForm />} />
          <Route path="/upload/docs" element={<lazyRoutes.uploadDocs />} />
          <Route path="/subscription" element={<lazyRoutes.Subscription />} />
          <Route path="/success" element={<PaymentSuccess />} />
          <Route path="/failed" element={<PaymentFail />} />

          <Route
            path="/terms-and-conditions"
            element={<lazyRoutes.Privacy />}
          />

          <Route path="/dashboard/*" element={<lazyRoutes.Layout />}>
            <Route
              path=""
              element={
                <ProtectedRoute allowedRoles={['admin', 'hr', 'employee']}>
                  <lazyRoutes.Home onOpenChat={() => setIsChatOpen(true)} />
                </ProtectedRoute>
              }
            />
            <Route path="admin">
              <Route
                index
                element={
                  <ProtectedRoute allowedRoles={['admin', 'hr']}>
                    <Navigate to="assign-roles" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="assign-roles"
                element={
                  <ProtectedRoute allowedRoles={['admin', 'hr']}>
                    <lazyRoutes.AssignRoles />
                  </ProtectedRoute>
                }
              />
              <Route
                path="update-permissions"
                element={
                  <ProtectedRoute allowedRoles={['admin', 'hr']}>
                    <lazyRoutes.UpdatePermissions />
                  </ProtectedRoute>
                }
              />
            </Route>

            <Route
              path="employees"
              element={
                <ProtectedRoute allowedRoles={['admin', 'hr']}>
                  <lazyRoutes.Employees />
                </ProtectedRoute>
              }
            />

            <Route
              path="grievances"
              element={
                <ProtectedRoute allowedRoles={['admin', 'hr', 'employee']}>
                  <lazyRoutes.Grievances />
                </ProtectedRoute>
              }
            />

            <Route
              path="profile-edit-requests"
              element={
                <ProtectedRoute allowedRoles={['admin', 'hr', 'employee']}>
                  <lazyRoutes.ProfileEditRequest />
                </ProtectedRoute>
              }
            />
            <Route
              path="ai-recruitment"
              element={
                <ProtectedRoute allowedRoles={['admin', 'hr']}>
                  <AiRecruiter />
                </ProtectedRoute>
              }
            />
            <Route
              path="ask-tara"
              element={
                <ProtectedRoute allowedRoles={['admin', 'hr']}>
                  <TaraAI />
                </ProtectedRoute>
              }
            />
            <Route
              path="gallery"
              element={
                <ProtectedRoute allowedRoles={['admin', 'hr']}>
                  <lazyRoutes.Gallery />
                </ProtectedRoute>
              }
            />
            <Route path="leavesAndOccasions">
              <Route index element={<Navigate to="occasions" />} />
              <Route
                path="occasions"
                element={
                  <ProtectedRoute allowedRoles={['admin', 'hr', 'employee']}>
                    <lazyRoutes.Calendar />{' '}
                  </ProtectedRoute>
                }
              />
            </Route>
            <Route
              path="leaveManagement"
              element={
                <ProtectedRoute allowedRoles={['admin', 'hr', 'employee']}>
                  <lazyRoutes.MyLeave />{' '}
                </ProtectedRoute>
              }
            />

            <Route
              path="manage-leaves"
              element={
                <ProtectedRoute allowedRoles={['admin', 'hr']}>
                  <ManageLeave />
                </ProtectedRoute>
              }
            />

            <Route
              path="slackStatus"
              element={
                <ProtectedRoute allowedRoles={['admin', 'hr']}>
                  <lazyRoutes.SlackActivity />
                </ProtectedRoute>
              }
            />
            <Route
              path="companydocs"
              element={
                <ProtectedRoute allowedRoles={['admin', 'hr', 'employee']}>
                  <lazyRoutes.PolicyDocs />{' '}
                </ProtectedRoute>
              }
            />

            <Route
              path="mark-attendance"
              element={
                <ProtectedRoute allowedRoles={['admin', 'hr', 'employee']}>
                  <lazyRoutes.AttendancePortal />{' '}
                </ProtectedRoute>
              }
            />
            <Route path="attendance-list">
              <Route
                index
                element={
                  <ProtectedRoute allowedRoles={['admin', 'hr']}>
                    <lazyRoutes.AttendanceList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="monthly-view"
                element={
                  <ProtectedRoute allowedRoles={['admin', 'hr']}>
                    <lazyRoutes.MonthlyView />
                  </ProtectedRoute>
                }
              />
            </Route>

            <Route
              path="view-attedence/:employeeId"
              element={
                <ProtectedRoute allowedRoles={['admin', 'hr']}>
                  <lazyRoutes.AttendenceDetail />
                </ProtectedRoute>
              }
            />

            <Route
              path="profile"
              element={
                <ProtectedRoute allowedRoles={['admin', 'hr', 'employee']}>
                  <lazyRoutes.Profile />{' '}
                </ProtectedRoute>
              }
            />
            <Route
              path="online-users"
              element={
                <ProtectedRoute allowedRoles={['admin', 'hr']}>
                  <lazyRoutes.UsersOnline />
                </ProtectedRoute>
              }
            />
            <Route
              path="regularize-attendance"
              element={
                <ProtectedRoute allowedRoles={['admin', 'hr']}>
                  <lazyRoutes.Regularize />
                </ProtectedRoute>
              }
            />

            <Route
              path="payroll"
              element={
                <ProtectedRoute allowedRoles={['admin', 'hr']}>
                  <lazyRoutes.Payroll />
                </ProtectedRoute>
              }
            />

            <Route
              path="settings"
              element={
                <ProtectedRoute allowedRoles={['admin', 'hr']}>
                  <lazyRoutes.Settings />{' '}
                </ProtectedRoute>
              }
            />
            <Route
              path="token-usage"
              element={
                <ProtectedRoute allowedRoles={['admin', 'hr']}>
                  <lazyRoutes.TokenUsage />{' '}
                </ProtectedRoute>
              }
            />
            <Route
              path="analytics"
              element={
                <ProtectedRoute allowedRoles={['admin', 'hr']}>
                  <lazyRoutes.Analytics />{' '}
                </ProtectedRoute>
              }
            />
            <Route
              path="projects"
              element={
                <ProtectedRoute allowedRoles={['admin', 'hr']}>
                  <lazyRoutes.Projects />{' '}
                </ProtectedRoute>
              }
            />
            <Route
              path="tasks"
              element={
                <ProtectedRoute allowedRoles={['employee']}>
                  <lazyRoutes.Tasks />{' '}
                </ProtectedRoute>
              }
            />
          </Route>

          <Route path="*" element={<lazyRoutes.PageNotFound />} />
          <Route path="/unauthorized" element={<lazyRoutes.Unauthorized />} />
        </Routes>
      </Suspense>
      <Suspense fallback={<div />}>
        <ToastContainer />
      </Suspense>
      <TermsModal isOpen={isTermsOpen} setIsTermsOpen={setIsTermsOpen} />
      <Consent isOpen={isConsentOpen} setIsOpen={setIsConsentOpen} />
      <AccessModal open={isAccessModalOpen} setOpen={setIsAccessModalOpen} />
    </div>
  );
};

const AppWrapper = () => (
  <QueryClientProvider client={QUERY_CLIENT}>
    <GoogleOAuthProvider
      clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID as string}
    >
      <ChatSessionProvider>
        <Router>
          <App />
        </Router>
      </ChatSessionProvider>
    </GoogleOAuthProvider>
  </QueryClientProvider>
);

export default AppWrapper;
